<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { EntityListPage, getAuthUsersLookup, type EntityConfig } from '@botto/core';
import { usersStore } from '../api';
import type { UserTariffRow } from '../api';
import { BILLING_TYPE_OPTIONS, billingTypeLabel } from '../constants/billing-types';
import { getCurrenciesLookup, getTariffsLookup } from '../lookups';

const listRef = ref<InstanceType<typeof EntityListPage> | null>(null);
const authUsersLookup = getAuthUsersLookup();
const tariffsLookup = getTariffsLookup();
const currenciesLookup = getCurrenciesLookup();

const config = computed<EntityConfig<UserTariffRow>>(() => ({
  title: 'Пользователь',
  titlePlural: 'Пользователи',
  popupWidth: 520,
  popupHeight: 460,
  noCreate: true,
  noDelete: true,
  columns: [
    {
      dataField: 'user_id',
      caption: 'ID',
      dataType: 'number',
      width: 90,
    },
    {
      dataField: 'email',
      caption: 'Email',
      width: 260,
      allowSorting: false,
      calculateDisplayValue: (row) => authUsersLookup.labelOf(row.user_id),
    },
    {
      dataField: 'balance',
      caption: 'Баланс',
      dataType: 'number',
      format: { type: 'fixedPoint', precision: 4 },
    },
    {
      dataField: 'currency',
      caption: 'Валюта',
      width: 100,
    },
    {
      dataField: 'tariff_id',
      caption: 'Тариф',
      allowSorting: false,
      calculateDisplayValue: (row) => tariffsLookup.labelOf(row.tariff_id),
    },
    {
      dataField: 'billing_type',
      caption: 'Скидка',
      allowSorting: false,
      calculateDisplayValue: (row) => billingTypeLabel(row.billing_type),
    },
  ],
  fields: [
    {
      dataField: 'user_id',
      label: 'Email',
      editOnly: true,
      lookup: authUsersLookup,
      editorOptions: { disabled: true, readOnly: true },
    },
    {
      dataField: 'balance',
      label: 'Баланс',
      editOnly: true,
      editorType: 'dxNumberBox',
      editorOptions: { format: { type: 'fixedPoint', precision: 4 } },
    },
    {
      dataField: 'currency',
      label: 'Валюта',
      required: true,
      lookup: currenciesLookup,
    },
    {
      dataField: 'tariff_id',
      label: 'Тариф',
      required: true,
      lookup: tariffsLookup,
    },
    {
      dataField: 'billing_type',
      label: 'Скидка',
      required: true,
      editorType: 'dxSelectBox',
      editorOptions: {
        items: [...BILLING_TYPE_OPTIONS],
        valueExpr: 'value',
        displayExpr: 'text',
      },
    },
  ],
}));

onMounted(async () => {
  await Promise.all([
    authUsersLookup.ensureLoaded(),
    tariffsLookup.ensureLoaded(),
    currenciesLookup.ensureLoaded(),
  ]);
  listRef.value?.refresh();
});
</script>

<template>
  <EntityListPage ref="listRef" :config="config" :store="usersStore" />
</template>
